import { ArrowLeftCircleIcon, ArrowRightCircleIcon, BellAlertIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { EmailTo } from 'components/EmailTo'
import { LayoutLoading } from 'components/LayoutLoading'
import type { InputType } from 'config'
import { IBrokerCondition } from 'pages/Admin'
import { EmailTemplates } from 'pages/Admin/ConditionsAndTemplates/EmailTemplates'
import { Notes } from 'pages/Admin/ConditionsAndTemplates/Notes'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { partiesMapWithAccountTypes } from 'reducers/loanDetail.reducer'
import { getLoanSubmissionNotify, sendLoanSubmissionNotify, setLoanSubmissionNotify } from 'services'
import { Button, ButtonGroup, Modal, PlainTable } from 'stories/components'
import { borrowerEmailParties, capitalizeFirstLetter, toCamelCase } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { requestedToParties } from '../../../pages/LoanSubmission/constants'
import type { LoanSubmissionCondition, LoanSubmissionDocument } from '../../../pages/LoanSubmission/types'
import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

export const timesOption = {
  '0': '12:00 am',
  '1': '01:00 am',
  '2': '02:00 am',
  '3': '03:00 am',
  '4': '04:00 am',
  '5': '05:00 am',
  '6': '06:00 am',
  '7': '07:00 am',
  '8': '08:00 am',
  '9': '09:00 am',
  '10': '10:00 am',
  '11': '11:00 am',
  '12': '12:00 pm',
  '13': '01:00 pm',
  '14': '02:00 pm',
  '15': '03:00 pm',
  '16': '04:00 pm',
  '17': '05:00 pm',
  '18': '06:00 pm',
  '19': '07:00 pm',
  '20': '08:00 pm',
  '21': '09:00 pm',
  '22': '10:00 pm',
  '23': '11:00 pm',
}

const defaultInputs = (): Record<string, InputType> => {
  return {
    notifyPeriod: {
      inputType: 'text',
      type: 'number',
      title: 'Days Reminder Frequency',
    },
    notifyTime: {
      inputType: 'select',
      title: 'Time',
      options: timesOption,
      allowDefaultOption: false,
      hasDefaultOption: true,
    },
    note: {
      inputType: 'textarea',
      title: 'Note',
      rows: 3,
    },
  }
}

let isUpdated = false

export const NotifyBorrowerSlide = ({
  conditions,
  brokerProfile,
  brokerConditions,
  templateNumber,
  documents,
  onVisibilityChange,
}: {
  conditions: LoanSubmissionCondition[]
  brokerProfile: boolean
  brokerConditions: IBrokerCondition[]
  templateNumber: number
  documents: LoanSubmissionDocument[]
  onVisibilityChange?: (visible: boolean) => void
}) => {
  const [isShowSlide, setShowSlide] = useState(false)
  const floater = useFloaterTab('notification', { side: 'left', percent: 20, offset: 60, mobileTop: 248 })
  const [inputs, setInputs] = useState(defaultInputs())
  const [isLoading, setLoading] = useState(true)
  const [action, setAction] = useState('')
  const [selectedEmails, setSelectedEmails] = useState<Record<string, 'to' | 'cc' | 'bcc' | ''>>({})
  const [emails, setEmails] = useState<{ party: string; email: string }[]>([])
  const [selectedParty, setSelectedParty] = useState('Borrower')
  const [notificationData, setNotificationData] = useState<Record<string, any>>({})
  const [showTemplate, setShowTemplate] = useState(false)
  const [templateType, setTemplateType] = useState<'note' | 'email'>('note')

  const { loanDetail, borrower } = useSelector((state: any) => ({
    loanDetail: state.loanDetail,
    borrower: state.borrower.borrower,
  }))
  const { parties, closeParties } = loanDetail

  const outstandingConditions = useMemo(() => {
    let rlt: any = []
    if (templateNumber === 0) {
      brokerConditions
        .filter(({ required, requested }) => !!required && !!requested)
        .map((cond) => {
          const document = documents.find((item) => item.conditionNo === cond.no)
          if (!document) rlt.push([cond.no, cond.name])
        })
    } else {
      conditions.map((cond) => {
        if (cond.requested && !cond.intext && !cond.cleared && cond.requestedTo === selectedParty) {
          rlt.push([cond.no, cond.name])
        }
      })
    }
    return rlt
  }, [conditions, brokerConditions, documents, templateNumber, selectedParty])

  useEffect(() => {
    const newInputs = cloneDeep(inputs)
    newInputs.note.value = notificationData.notifyNotes?.[selectedParty] || ''
    newInputs.notifyPeriod.value = notificationData.notifyPeriods?.[selectedParty] ?? ''
    newInputs.notifyTime.value = notificationData.notifyTimes?.[selectedParty] ?? ''
    Object.keys(newInputs).map((key) => {
      newInputs[key].disabled = brokerProfile
    })
    setInputs(newInputs)
    setSelectedEmails(notificationData.notifyEmail?.[selectedParty] || {})
  }, [selectedParty, notificationData])

  useEffect(() => {
    // The borrower is offered at every address on file, not just the primary: a borrower who lists
    // additional addresses expects the notification at all of them. Other parties keep their one
    // address.
    const emails: { party: string; email: string }[] =
      selectedParty === 'Borrower' ? borrowerEmailParties(borrower, selectedParty) : []
    if (selectedParty !== 'Borrower' && selectedParty !== 'Broker') {
      const partyEmail = closeParties[capitalizeFirstLetter(toCamelCase(selectedParty))]?.EMail
      if (partyEmail) emails.push({ party: selectedParty, email: partyEmail })
    }
    const partiesMap = partiesMapWithAccountTypes()

    Object.keys(parties).map((key) => {
      const { email } = parties[key]
      if (!email) return

      emails.push({
        party: partiesMap[key],
        email,
      })
    })
    setEmails(emails)
  }, [parties, borrower, selectedParty])

  const refetch = () => {
    setLoading(true)
    getLoanSubmissionNotify()
      .then(
        (data: {
          notifyNotes: Record<string, any>
          notifyEmail: any
          notifyPeriods: Record<string, any>
          notifyTimes: Record<string, any>
        }) => {
          setNotificationData(data)
        },
      )
      .finally(() => setLoading(false))
  }

  const onShowSlide = () => {
    const nextState = !isShowSlide
    setShowSlide(nextState)
    onVisibilityChange?.(nextState)
    if (!nextState) setShowTemplate(false)
    if (nextState) refetch()
  }

  const onChange = (key: string, v: any) => {
    const newInputs = cloneDeep(inputs)
    if (key === 'notifyPeriod') {
      if (v < 0) v = 0
    }
    newInputs[key].value = v
    setInputs(newInputs)
    isUpdated = true
  }

  const onChooseTemplate = (template: Record<string, any>) => {
    const temp = cloneDeep(inputs)
    if (templateType === 'note') {
      temp.note.value = template.notes
    } else {
      temp.note.value = template.content
    }
    setInputs(temp)
    setShowTemplate(false)
    isUpdated = true
  }

  const openTemplateModal = () => {
    setTemplateType('note')
    setShowTemplate(true)
  }

  const onSave = () => {
    if (!isUpdated) return
    const data = {
      notifyNotes: { ...notificationData.notifyNotes, [selectedParty]: inputs.note.value },
      notifyPeriods: { ...notificationData.notifyPeriods, [selectedParty]: inputs.notifyPeriod.value },
      notifyTimes: { ...notificationData.notifyTimes, [selectedParty]: inputs.notifyTime.value },
    }
    setLoading(true)
    setLoanSubmissionNotify(data).finally(() => setLoading(false))
    isUpdated = false
  }

  const vendorParties = ['Title Company', 'Hazard Insurance Company', 'Escrow Company', 'Appraiser']

  const onSubmit = async () => {
    setAction('notify')
    sendLoanSubmissionNotify(selectedParty)
      .then(() => {
        const partyLabel = requestedToParties[selectedParty]
        toast(`Notification is sent to ${partyLabel}.`, { type: 'info' })
        if (vendorParties.includes(selectedParty)) {
          toast(`A Vendor account has been created and login credentials have been emailed to ${partyLabel}.`, {
            type: 'success',
          })
        }
      })
      .finally(() => setAction(''))
  }

  const updateEmails = async (data: any) => {
    await setLoanSubmissionNotify({
      notifyEmail: { ...notificationData.notifyEmail, [selectedParty]: data },
    })
    return true
  }

  const hasToEmail = useMemo(() => {
    let rlt = false
    try {
      Object.keys(selectedEmails).map((key: string) => {
        if (selectedEmails[key] === 'to') rlt = true
      })
    } catch {}
    return rlt
  }, [selectedEmails])

  return (
    <>
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ring-shade-blue bg-shade-blue text-white border hover:bg-white border-shade-blue hover:text-shade-blue rounded flex items-center gap-2 w-auto sm:w-[155px]`}
          {...floater.tabProps(onShowSlide)}
        >
          <BellAlertIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Notification</span>
        </button>
      </FloaterTabWrapper>
      {isShowSlide && (
        <>
          <div
            className={`z-50 shadow1 fixed bg-white border top-[5vh] bottom-0 w-[24rem] max-w-full h-[90vh] overflow-auto ease-in-out ${floaterPanelEdgeClass(
              floater.side,
            )}`}
          >
            <LayoutLoading show={isLoading} />
            <div className="w-[24rem] max-w-full z-20 rounded-t flex justify-between p-3 font-bold text-sm place-items-center items-center text-shade-blue border-b bg-white">
              <div className="flex items-center">
                <p className="text-lg text-gray-800">Notification</p>
              </div>
              <button className="hover:underline hover-shadow1 p-1 rounded" onClick={onShowSlide}>
                {floater.side === 'left' ? (
                  <ArrowLeftCircleIcon className="w-5 h-5" />
                ) : (
                  <ArrowRightCircleIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            <ButtonGroup title={requestedToParties} value={selectedParty} onChange={setSelectedParty} />
            <div className="h-fit overflow-auto relative text-sm p-3 pb-0"></div>
            <div className="px-3">
              <EmailTo
                Type={'show'}
                CanEdit={!brokerProfile}
                Emails={selectedEmails}
                Data={emails}
                onChange={(data: any) => setSelectedEmails(data)}
                onUpdate={updateEmails}
              />
            </div>
            <div className="flex justify-center px-4 mt-4">
              <Button
                className="w-full"
                loading={action == 'notify'}
                disabled={!outstandingConditions.length || !hasToEmail}
                onClick={onSubmit}
              >
                <span>Notify Now</span>
              </Button>
            </div>

            {Object.keys(inputs).map((key) => {
              const input = inputs[key]
              const inputForRender =
                key === 'note'
                  ? {
                      ...input,
                      additionalElements: (
                        <span className="text-shade-blue hover:underline cursor-pointer" onClick={openTemplateModal}>
                          Choose Template
                        </span>
                      ),
                    }
                  : input
              return (
                <div className="p-2" key={key}>
                  <RenderInput Key={key} input={inputForRender} onChange={onChange} onBlur={onSave} />
                </div>
              )
            })}

            <div className="p-4">
              <p className="font-semibold mb-2 border-b italic text-[15px]">
                Requested Outstanding Conditions
                <span className="ml-1">({outstandingConditions.length})</span>
              </p>
              <div>
                <PlainTable
                  header={['Condition #', 'Description']}
                  data={outstandingConditions}
                  tdClass="px-4 py-2 break-spaces text-[13px]"
                />
              </div>
            </div>
          </div>
          {showTemplate && (
            <Modal
              title={templateType === 'note' ? 'Note Template' : 'Email Template'}
              titleOkay=""
              onClose={() => setShowTemplate(false)}
              isOpen
            >
              <div className="min-w-[600px]">
                <div className="mb-3">
                  <ButtonGroup
                    title={{ note: 'Note Templates', email: 'Email Templates' }}
                    value={templateType}
                    onChange={(v) => setTemplateType(v as 'note' | 'email')}
                  />
                </div>
                {templateType === 'note' ? (
                  <Notes isModal onRawClick={onChooseTemplate} />
                ) : (
                  <EmailTemplates isModal onRawClick={onChooseTemplate} />
                )}
              </div>
            </Modal>
          )}
        </>
      )}
    </>
  )
}
